import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../../components/SideBar';
import Header from '../../components/Header';
import useGlobale from '../../components/global';
import DemandeurMenu from '../../components/DemandeurMenu';
import api from '../../components/api';

import DashboardHeader from './DashboardHeader';
import StatCards from './StatCards';
import DemographicStats from './DemographicStats';
import RecentDemandeursTable from './RecentDemandeursTable';
import TopDomains from './TopDomains';
import { InscriptionsChart, DiplomeChart, PieChartCard, CompetencesChart, AgeChart, NationalitesChart } from './charts';
import { colors } from './constants';

const DashboardAdmin = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useGlobale();
  const [isLoading, setIsLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [error, setError] = useState(null);

  // Fetch statistics from API
  useEffect(() => {
    const fetchStatistics = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('demandeur/dashboard-statistics/');
        setStatistics(response.data);
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des statistiques:', err);
        setError('Impossible de charger les statistiques');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  // Set sidebar initial state based on screen size
  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Statistiques calculées
  const computedStatistics = useMemo(() => {
    if (!statistics) return null;

    return {
      totalDemandeurs: statistics.totalDemandeurs,
      totalAvecProfil: statistics.totalAvecProfil || 0,
      totalSansProfil: statistics.totalSansProfil || 0,
      hommes: statistics.hommes,
      femmes: statistics.femmes,
      pourcentageHommes: statistics.pourcentageHommes,
      pourcentageFemmes: statistics.pourcentageFemmes,
    };
  }, [statistics]);

  // Render content based on loading and error states
  const renderContent = () => {
    if (error) {
      return (
        <div className="bg-red-50 p-4 rounded-lg text-red-600">
          {error}
        </div>
      );
    }

    if (isLoading || !statistics) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2" 
               style={{borderColor: colors.primary}}>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <StatCards statistics={statistics} computedStatistics={computedStatistics} />
        <DemographicStats statistics={statistics} />
        
        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <InscriptionsChart data={statistics.inscriptionsMensuelles} />
          <DiplomeChart data={statistics.diplomeDistribution} />
        </div>

        {/* Charts Row 2 - Pie Charts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <PieChartCard 
            data={statistics.languesStats} 
            title="Répartition des langues"
            gradientColors={colors.gradient.blue}
          />
          <PieChartCard 
            data={statistics.situationFamiliale} 
            title="Situation familiale"
            gradientColors={colors.gradient.pink}
          />
          <PieChartCard 
            data={[
              { name: 'PDF', value: statistics.documentsParType?.pdf || 0 },
              { name: 'Word', value: statistics.documentsParType?.word || 0 },
              { name: 'Image', value: statistics.documentsParType?.image || 0 }
            ]} 
            title="Types de documents"
            gradientColors={colors.gradient.purple}
          />
        </div>

        {/* Charts Row 3 - Nationalites & Age */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <NationalitesChart data={statistics.nationaliteStats} />
          <AgeChart data={statistics.ageGroups} />
        </div>

        {/* Charts Row 4 - Skills */}
        <CompetencesChart data={statistics.topCompetences} />

        {/* Full width - Recent Demandeurs */}
        <RecentDemandeursTable recentDemandeurs={statistics.recentDemandeurs} />

        <TopDomains topDomains={statistics.topDomains} totalExperiences={statistics.totalExperiences} />
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-[#F4F8E9]">
      <Sidebar
        isOpen={isSidebarOpen}
        menuItems={DemandeurMenu()}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col w-full">
        <Header
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          user={user}
        />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8 w-full">
          <DashboardHeader />
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default DashboardAdmin;
